/**
 * Bargeldabgleich je Kasse (X5, Plan 08): reine Rechnung in ganzen Cent, ohne Datenbank und ohne Fließkomma.
 * BARSO ist kein zertifiziertes Kassensystem und nimmt keine Zahlungen ab: Der Abgleich ist eine Hilfe, um gezähltes Bargeld
 * mit dem zu vergleichen, was die Kasse als kassiert gemeldet hat. Es ist kein Kassenabschluss im Sinne der KassenSichV.
 *
 * erwartet(t) = Σ Wechselgeld(≤ t) − Σ Entnahmen(≤ t) + bar kassiert(t)
 * bar kassiert(t) = Σ Preise der Positionen mit Verkaufszeit ≤ t, ohne die bis t stornierten (Storno ≤ t) und ohne Positionen mit Zahlart „Karte“ (U14)
 * Differenz = gezählt − erwartet(t)   (positiv = Überschuss, negativ = Fehlbetrag)
 *
 * Konfliktzeilen zählen mit (das Geld ist kassiert). Ein Storno mindert immer die Kasse des ursprünglichen Verkaufs.
 */

import type { PaymentMethod } from './payment.js';

export const CASH_ENTRY_KINDS = ['float', 'withdrawal', 'count'] as const;
export type CashEntryKind = (typeof CASH_ENTRY_KINDS)[number];

/** Feste Stückelung in Cent: 1, 2, 5, 10, 20, 50 Cent, 1, 2 € und Scheine zu 5, 10, 20, 50, 100, 200 €. */
export const CASH_DENOMINATIONS = [
  1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10_000, 20_000,
] as const;
export type CashDenomination = (typeof CASH_DENOMINATIONS)[number];

/** Höchstzahl je Stückelung (Schutz vor Tippfehlern, die die Summe sprengen). */
export const CASH_MAX_COUNT_PER_DENOMINATION = 100_000;
/** Höchstbetrag einer Zeile in Cent (1.000.000 €). */
export const CASH_MAX_AMOUNT_CENTS = 100_000_000;

/** Eine gültige Zeile (nicht ersetzt) mit Zeitpunkt in Millisekunden seit 1970 (UTC). */
export interface CashMovement {
  kind: 'float' | 'withdrawal';
  amountCents: number;
  atMs: number;
}

/** Eine Verkaufsposition: Preis, Verkaufszeit (Uhr des Geräts) und Zeitpunkt des Stornos (`null` = nicht storniert). */
export interface CashSaleLine {
  priceCents: number;
  soldAtMs: number;
  voidedAtMs: number | null;
  /** Zahlart (U14); fehlt sie, gilt die Position als bar (so war es vor U14). Kartenpositionen zählen nie zum Bargeld. */
  payment?: PaymentMethod;
}

function assertCents(value: number, name: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${name} muss eine nicht negative ganze Zahl (Cent) sein.`);
  }
}

/** Summe einer Stückelung `{ "5000": 2, "100": 3 }` in Cent. Unbekannte Schlüssel und ungültige Anzahlen werfen. */
export function sumBreakdown(breakdown: Readonly<Record<string, number>>): number {
  let total = 0;
  for (const [key, count] of Object.entries(breakdown)) {
    const value = Number(key);
    if (!(CASH_DENOMINATIONS as readonly number[]).includes(value) || String(value) !== key) {
      throw new RangeError(`Unbekannte Stückelung: ${key}`);
    }
    if (!Number.isInteger(count) || count < 0 || count > CASH_MAX_COUNT_PER_DENOMINATION) {
      throw new RangeError(`Ungültige Anzahl für ${key}.`);
    }
    total += value * count;
  }
  return total;
}

/**
 * Bar kassiert bis zum Zeitpunkt `atMs`: Positionen, die bis dahin verkauft und nicht bis dahin storniert wurden.
 * Ein Storno nach `atMs` ändert nichts an der Rechnung für `atMs`.
 */
export function cashSalesAt(lines: readonly CashSaleLine[], atMs: number): number {
  let total = 0;
  for (const line of lines) {
    assertCents(line.priceCents, 'priceCents');
    if (line.payment === 'card') continue;
    if (line.soldAtMs > atMs) continue;
    if (line.voidedAtMs !== null && line.voidedAtMs <= atMs) continue;
    total += line.priceCents;
  }
  return total;
}

/** Summe der mit „Karte“ markierten Positionen zum Zeitpunkt `atMs` (nur Anzeige, nie Teil des Bargeldabgleichs). */
export function cardSalesAt(lines: readonly CashSaleLine[], atMs: number): number {
  let total = 0;
  for (const line of lines) {
    if (line.payment !== 'card') continue;
    assertCents(line.priceCents, 'priceCents');
    if (line.soldAtMs > atMs) continue;
    if (line.voidedAtMs !== null && line.voidedAtMs <= atMs) continue;
    total += line.priceCents;
  }
  return total;
}

/** Erwarteter Bargeldbestand einer Kasse zum Zeitpunkt `atMs` (`Number.POSITIVE_INFINITY` = alles bis jetzt). */
export function expectedCashAt(
  movements: readonly CashMovement[],
  lines: readonly CashSaleLine[],
  atMs: number,
): number {
  let expected = 0;
  for (const movement of movements) {
    assertCents(movement.amountCents, 'amountCents');
    if (movement.atMs > atMs) continue;
    expected += movement.kind === 'float' ? movement.amountCents : -movement.amountCents;
  }
  return expected + cashSalesAt(lines, atMs);
}

/** Differenz: positiv = Überschuss, negativ = Fehlbetrag. */
export function cashDifference(countedCents: number, expectedCents: number): number {
  assertCents(countedCents, 'countedCents');
  if (!Number.isSafeInteger(expectedCents)) {
    throw new RangeError('expectedCents muss eine ganze Zahl (Cent) sein.');
  }
  return countedCents - expectedCents;
}

/** Eine Zeile mit ihrer Korrekturkette: `supersedesId` zeigt auf die ersetzte Zeile. */
export interface CashEntryLink {
  id: string;
  supersedesId: string | null;
}

/** Zeilen, die nicht durch eine Korrektur ersetzt wurden (jede Zeile lässt sich nur einmal ersetzen). */
export function activeCashEntries<T extends CashEntryLink>(entries: readonly T[]): T[] {
  const replaced = new Set(
    entries.map((entry) => entry.supersedesId).filter((id): id is string => id !== null),
  );
  return entries.filter((entry) => !replaced.has(entry.id));
}

/**
 * Eine Zählung ist vorläufig, wenn nach ihrer Erfassung (`recordedAtMs`) noch etwas dazukam, das zum Zählzeitpunkt
 * (`countedAtMs`) schon galt oder es berührt: ein später angekommener Verkauf (`receivedAtMs` > `recordedAtMs` und `soldAtMs` ≤
 * `countedAtMs`), ein nach der Erfassung erfolgter Storno eines Verkaufs aus der Zählzeit (`voidedAtMs` > `recordedAtMs`,
 * `soldAtMs` ≤ `countedAtMs`), eine offene Quarantäne der Kasse (gesicherter Verkauf, nicht abgeglichen) oder eine Kasse, die
 * noch Verkäufe meldet, die der Server nicht hat (`registerBehind`). Die Differenz wird dann aus dem aktuellen Stand berechnet.
 */
export function isCountProvisional(
  lines: readonly { soldAtMs: number; receivedAtMs: number; voidedAtMs?: number | null }[],
  countedAtMs: number,
  recordedAtMs: number,
  registerBehind: boolean,
  openQuarantine = false,
): boolean {
  if (registerBehind || openQuarantine) return true;
  return lines.some(
    (line) =>
      line.soldAtMs <= countedAtMs &&
      (line.receivedAtMs > recordedAtMs ||
        (line.voidedAtMs !== null &&
          line.voidedAtMs !== undefined &&
          line.voidedAtMs > recordedAtMs)),
  );
}
